import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogIn, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { COMPANY } from "@/lib/constants";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Bienvenido");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Error de autenticación";
      toast.error(typeof msg === "string" ? msg : "Error de autenticación");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <div className="absolute top-6 left-6">
        <a
          href="/"
          data-testid="admin-login-back"
          className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white"
        >
          <ArrowLeft size={14} /> Volver al sitio
        </a>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <img src={COMPANY.logo_url} alt="Modolo" className="h-12 w-auto" />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-xl">Modolo</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/60">Panel admin</div>
            </div>
          </div>

          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3">
            Acceso al panel
          </h1>
          <p className="text-white/60 text-sm mb-8">
            Inicia sesión para gestionar las fotos y los pedidos de presupuesto.
          </p>

          <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                data-testid="admin-login-email"
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/25 focus:border-[#1F9D40] outline-none py-3 text-base text-white placeholder-white/30"
                placeholder="tu@empresa.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold mb-2">
                Contraseña
              </label>
              <input
                type="password"
                required
                value={password}
                data-testid="admin-login-password"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/25 focus:border-[#1F9D40] outline-none py-3 text-base text-white placeholder-white/30"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1F9D40] hover:bg-[#177d33] disabled:opacity-70 text-white font-bold py-4 rounded-full transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Entrando…
                </>
              ) : (
                <>
                  <LogIn size={16} /> Entrar
                </>
              )}
            </button>

            <p className="text-xs text-white/40 text-center flex items-center justify-center gap-2">
              <Lock size={12} /> Conexión privada · Modolo Multiservices
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
